import React, { useState, useEffect, useRef } from 'react';
import { invokeSyncRIDBFacilities, checkFacilitiesCount } from '../services/facilitiesSync';
import { checkSyncStatus } from '../services/supabaseFacilitiesSearch';
import { supabase } from '../services/supabase';

const FacilitiesSyncAdmin = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [facilitiesCount, setFacilitiesCount] = useState(0);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      console.log('Starting sync...');
      const result = await invokeSyncRIDBFacilities();
      console.log('Sync result:', result);
      setSyncStatus('completed');
      
      // Check count after sync
      await checkStatusNow();
    } catch (err) {
      console.error('Sync failed:', err);
      setError(err.message);
      setSyncStatus('error');
    } finally {
      setSyncing(false);
    }
  };

  const checkStatusNow = async () => {
    try {
      const status = await checkSyncStatus();
      setFacilitiesCount(status.count);
      if (status.synced) {
        setSyncStatus('completed');
      } else {
        setSyncStatus('pending');
      }
    } catch (err) {
      console.error('Status check failed:', err);
      setError(err.message);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      console.log(`Processing ${file.name}...`);
      
      const text = await file.text();
      let data = [];

      // Parse CSV or JSON
      if (file.name.endsWith('.csv')) {
        data = parseCSV(text);
      } else if (file.name.endsWith('.json')) {
        data = JSON.parse(text);
        if (!Array.isArray(data)) {
          data = data.RECDATA || [];
        }
      } else {
        throw new Error('Unsupported file format. Use CSV or JSON.');
      }

      console.log(`Parsed ${data.length} facilities from file`);

      if (data.length === 0) {
        throw new Error('No data found in file');
      }

      // Transform data to match database schema
      const transformedData = data.map(f => ({
        facility_id: parseInt(f.FacilityID) || parseInt(f.facility_id),
        facility_name: f.FacilityName || f.facility_name,
        facility_description: f.FacilityDescription || f.facility_description,
        facility_type_description: f.FacilityTypeDescription || f.facility_type_description,
        facility_use_fee_description: f.FacilityUseFeeDescription || f.FacilityUseFeesDescription || f.facility_use_fee_description,
        facility_phone: f.FacilityPhone || f.facility_phone,
        facility_email: f.FacilityEmail || f.facility_email,
        facility_directions: f.FacilityDirections || f.facility_directions,
        facility_url: f.FacilityReservationURL || f.FacilityMapURL || f.FacilityWebsiteURL || f.facility_url,
        facility_ada_access: f.FacilityAdaAccess || f.facility_ada_access,
        facility_accessibility_text: f.FacilityAccessibilityText || f.facility_accessibility_text,
        reservable: f.Reservable || f.reservable,
        day_use_fee_description: f.DayUseFeesDescription || f.day_use_fee_description,
        facility_latitude: parseFloat(f.FacilityLatitude) || parseFloat(f.facility_latitude) || null,
        facility_longitude: parseFloat(f.FacilityLongitude) || parseFloat(f.facility_longitude) || null,
        rec_area_id: parseInt(f.ParentRecAreaID) || parseInt(f.RecAreaID) || parseInt(f.rec_area_id) || null,
        organization_id: parseInt(f.ParentOrgID) || parseInt(f.OrgFacilityID) || parseInt(f.OrganizationID) || parseInt(f.organization_id) || null,
      })).filter(f => !isNaN(f.facility_id) && f.facility_id > 0); // Only keep rows with valid numeric facility_id

      // Upload to Supabase in batches
      const batchSize = 100;
      for (let i = 0; i < transformedData.length; i += batchSize) {
        const batch = transformedData.slice(i, i + batchSize);
        console.log(`Uploading batch ${Math.floor(i / batchSize) + 1}...`);
        
        const { error: uploadError } = await supabase
          .from('facilities')
          .upsert(batch, { onConflict: 'facility_id' });

        if (uploadError) {
          throw uploadError;
        }
      }

      console.log(`✓ Successfully uploaded ${transformedData.length} facilities`);
      setSyncStatus('completed');
      await checkStatusNow();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload file');
      setSyncStatus('error');
    } finally {
      setUploading(false);
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]);
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length > 0) {
        const obj = {};
        headers.forEach((header, idx) => {
          obj[header] = values[idx] || '';
        });
        rows.push(obj);
      }
    }

    return rows;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          insideQuotes = !insideQuotes;
        }
      } else if (char === '\t' && !insideQuotes) {
        // Tab-delimited (RIDB uses tabs)
        result.push(current.replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }

    // Add the last field
    result.push(current.replace(/^"|"$/g, ''));
    return result;
  };

  useEffect(() => {
    // Check initial status
    checkStatusNow();
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      padding: '16px', 
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      backgroundColor: '#f5f5f5',
      overflow: 'auto'
    }}>
      <h2 style={{ marginTop: 0, marginBottom: '16px' }}>🔄 Facilities Sync Admin</h2>
      
      <div style={{ 
        marginBottom: '12px', 
        padding: '12px', 
        backgroundColor: '#fff', 
        borderRadius: '4px',
        border: '1px solid #ddd'
      }}>
        <p style={{ margin: '0 0 8px 0' }}><strong>Current Status:</strong></p>
        <p style={{ margin: '0 0 8px 0' }}>
          {syncStatus === 'completed' ? (
            <span style={{ color: 'green' }}>✓ Sync completed</span>
          ) : syncStatus === 'pending' ? (
            <span style={{ color: 'orange' }}>⏳ Sync pending or in progress</span>
          ) : syncStatus === 'error' ? (
            <span style={{ color: 'red' }}>✗ Sync error</span>
          ) : (
            <span>Checking...</span>
          )}
        </p>
        <p style={{ margin: 0 }}><strong>Facilities:</strong> {facilitiesCount.toLocaleString()}</p>
      </div>

      {error && (
        <div style={{ 
          marginBottom: '12px', 
          padding: '10px', 
          backgroundColor: '#ffebee', 
          borderRadius: '4px', 
          color: 'red',
          fontSize: '12px',
          border: '1px solid #ff6b6b'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <button
          onClick={handleSync}
          disabled={syncing || uploading}
          style={{
            padding: '8px 16px',
            backgroundColor: syncing ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            flex: 1
          }}
        >
          {syncing ? 'Syncing...' : 'API Sync'}
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || syncing}
          style={{
            padding: '8px 16px',
            backgroundColor: uploading ? '#ccc' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            flex: 1
          }}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </button>

        <button
          onClick={checkStatusNow}
          disabled={uploading || syncing}
          style={{
            padding: '8px 16px',
            backgroundColor: '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            flex: 1
          }}
        >
          Refresh
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.json"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <div style={{ 
        marginTop: 'auto',
        padding: '10px', 
        backgroundColor: '#e3f2fd', 
        borderRadius: '4px',
        fontSize: '11px', 
        color: '#333',
        border: '1px solid #90caf9'
      }}>
        <strong>ℹ️ Info:</strong> Upload CSV/JSON or use API Sync. API syncs ~10K+ facilities in 2-5 min.
      </div>
    </div>
  );
};

export default FacilitiesSyncAdmin;
